import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

/** Admin page opened via a magic-link email — /admin/q/:token */
export default function AdminQuestion() {
  const { token } = useParams();
  const [q, setQ] = useState(null);
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [doneAction, setDoneAction] = useState("");

  useEffect(() => {
    document.title = "Moderate Question — Rubiemaze Admin";
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/questions/${token}`);
        setQ(res.data);
        setAnswer(res.data.answer || "");
      } catch (err) {
        setError(
          err?.response?.status === 404
            ? "This moderation link is invalid or has been revoked."
            : "Couldn't load the question. Try again.",
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const perform = async (action) => {
    if (action === "approve_publish" && answer.trim().length < 3) {
      toast.error("Type your answer before publishing.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/questions/${token}`, {
        action,
        answer: answer.trim(),
      });
      setQ(res.data);
      setDoneAction(action);
      toast.success(
        action === "reject"
          ? "Question rejected."
          : action === "approve_only"
          ? "Question approved — visible on the wall (no reply yet)."
          : "Reply published — live on the wall.",
      );
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Try again in a moment.";
      toast.error(typeof msg === "string" ? msg : "Action failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <section className="min-h-screen bg-[#030303] pt-40 text-center text-white/50 text-sm">
        Loading moderation view…
      </section>
    );
  }

  if (error) {
    return (
      <section className="min-h-screen bg-[#030303] pt-40 pb-20 text-center px-6">
        <div className="max-w-md mx-auto">
          <div className="font-display text-white text-3xl">Not accessible</div>
          <p className="mt-4 text-white/60 text-sm">{error}</p>
          <Link
            to="/"
            className="mt-8 inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#f3d568]"
          >
            Back home
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section
      data-testid="admin-question"
      className="relative min-h-screen bg-[#030303] pt-28 md:pt-36 pb-24"
    >
      <div className="max-w-3xl mx-auto px-6 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">
            Moderation
          </div>
          <h1 className="mt-4 font-display text-white text-3xl md:text-5xl leading-tight">
            Review question from {q.name}
          </h1>
          <div className="mt-3 text-white/45 text-[11px] uppercase tracking-[0.24em]">
            {q.city ? q.city + " · " : ""}Submitted {fmt(q.created_at)} ·{" "}
            <span
              className={
                q.status === "approved"
                  ? "text-[#D4AF37]"
                  : q.status === "rejected"
                  ? "text-red-300"
                  : "text-white/80"
              }
            >
              {q.status}
            </span>
          </div>
        </motion.div>

        <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8">
          <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 mb-3">
            The question
          </div>
          <p
            data-testid="admin-question-text"
            className="text-white text-[16px] md:text-lg leading-relaxed whitespace-pre-wrap"
          >
            {q.question}
          </p>
        </div>

        {q.status === "rejected" ? (
          <div
            data-testid="admin-status-rejected"
            className="mt-8 rounded-2xl border border-red-500/30 bg-red-500/5 p-6 text-center"
          >
            <div className="font-display text-white text-xl">
              Question rejected
            </div>
            <p className="mt-2 text-white/60 text-sm">
              This question will not appear on the public wall.
            </p>
          </div>
        ) : (
          <>
            <div className="mt-10">
              <label className="text-[10px] uppercase tracking-[0.28em] text-white/45">
                Your reply {q.status === "approved" && q.answer ? "(edit)" : ""}
              </label>
              <textarea
                data-testid="admin-answer"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                rows={6}
                placeholder="Reply as Ritesh — this is what appears publicly under the question."
                className="line-input mt-1 resize-none"
                maxLength={3000}
              />
              <div className="mt-1 text-right text-[11px] text-white/35 tabular-nums">
                {answer.length}/3000
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => perform("approve_publish")}
                disabled={submitting}
                data-testid="admin-approve-publish"
                className="group inline-flex items-center justify-center gap-3 rounded-full bg-[#D4AF37] text-black px-6 py-3.5 text-sm font-medium hover:bg-[#f3d568] transition-colors disabled:opacity-50 flex-1"
              >
                {q.status === "approved" && q.answer
                  ? "Update Reply"
                  : "Approve & Publish Reply"}
              </button>
              <button
                onClick={() => perform("approve_only")}
                disabled={submitting}
                data-testid="admin-approve-only"
                className="inline-flex items-center justify-center gap-3 rounded-full border border-white/20 text-white px-6 py-3.5 text-sm font-medium hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors disabled:opacity-50"
              >
                Approve without answer
              </button>
              <button
                onClick={() => perform("reject")}
                disabled={submitting}
                data-testid="admin-reject"
                className="inline-flex items-center justify-center gap-3 rounded-full border border-red-500/30 text-red-300 px-5 py-3.5 text-sm font-medium hover:border-red-500 hover:text-red-200 transition-colors disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </>
        )}

        {doneAction && (
          <div className="mt-8 text-center text-white/50 text-sm">
            You can safely close this tab.
            <div className="mt-3">
              <Link
                to="/questions"
                className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#f3d568]"
              >
                View the public wall →
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
